import { create, tsx } from '@dojo/framework/core/vdom';
import { CareerPosition } from '../../widgets/CareerPosition';
import { ContentWrapper } from '../../widgets/ContentWrapper';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { jobs } from './jobs';
import { Section } from '../../widgets/Section';
import { Text } from '../../widgets/Text';

import * as css from './Careers.m.css';
import SummaryList from '../../widgets/SummaryList';
import Image from '../../widgets/Image';

const commuteIcon = require('../../assets/images/icon-solid/career-icon-commute.svg');
const dentalIcon = require('../../assets/images/icon-solid/career-icon-dental.svg');
const hoursIcon = require('../../assets/images/icon-solid/career-icon-hours.svg');
const peopleIcon = require('../../assets/images/icon-solid/career-icon-team.svg');
const loveIcon = require('../../assets/images/icon-solid/career-icon-love.svg');
const spendingIcon = require('../../assets/images/icon-solid/career-icon-fsa.svg');
const pensionIcon = require('../../assets/images/icon-solid/career-icon-401k.svg');
const insuranceIcon = require('../../assets/images/icon-solid/career-icon-health.svg');
const vacationIcon = require('../../assets/images/icon-solid/career-icon-vacation.svg');
const internetIcon = require('../../assets/images/icon-solid/career-icon-internet.svg');
const lifeinsuranceIcon = require('../../assets/images/icon-solid/career-icon-life.svg');
const officesubsidyIcon = require('../../assets/images/icon-solid/career-icon-office.svg');
const parentalleaveIcon = require('../../assets/images/icon-solid/career-icon-parental.svg');
const visionIcon = require('../../assets/images/icon-solid/career-icon-vision.svg');

const doneImage = require('../../assets/images/icon-illustration/getting-things-done.svg');
const projectsImage = require('../../assets/images/icon-illustration/leading-edge.svg');
const teamImage = require('../../assets/images/icon-illustration/amazing-team.svg');

const GridColumns = create().properties<{ columnCount: 1 | 2 | 3 }>()(function GridColumns({
	children,
	properties
}) {
	const { columnCount } = properties();
	return (
		<div
			classes={[
				css.grid,
				columnCount === 1 && css.oneColumnGrid,
				columnCount === 2 && css.twoColumnGrid,
				columnCount === 1 && css.threeColumnGrid
			]}
		>
			{children()}
		</div>
	);
});

const factory = create();

export const Careers = factory(function Careers() {
	return (
		<div classes={css.root}>
			<Head
				title="JavaScript Development Jobs &amp; Careers | SitePen"
				description="SitePen is looking for enthusiastic contributors who love to solve problems and are team-oriented. Learn more about our culture and how to be a part of it here."
			/>
			<Hero paddingTop="sm" paddingBottom="md" background="level2PurpleTeal" align="center">
				{{
					xlTitle: 'Join Our Team'
				}}
			</Hero>
			<Section type="leading">
				<ContentWrapper size="smaller">
					<SummaryList>
						{{
							items: [
								{
									title: 'Leading Edge Projects',
									image: <Image src={projectsImage} align="center" size="icon" />,
									blurb:
										'While everyone else was building apps with Angular.js or Backbone, we were busy working on modern full-stack JavaScript and TypeScript architectures, prioritizing developer ergonomics, and developing frameworks for high-performance, sustainable Web apps. We invent tomorrow’s technologies to make the Web better, faster, and easier every day. And you can be part of it!'
								},
								{
									title: 'An Amazing Team',
									image: (
										<Image lazy src={teamImage} align="center" size="icon" />
									),
									blurb:
										'SitePen has one of the most talented and engaged software development teams around. If you’re self-motivated, know how to throw out a bad pun, get a kick out of Internet memes, and have a passion for Web development, solving difficult problems, and building open source software, you’ll fit right in.'
								},
								{
									title: 'Getting Things Done',
									image: (
										<Image lazy src={doneImage} align="center" size="icon" />
									),
									blurb:
										'When you’re part of the SitePen team, you’ll always be working on something new and interesting. Our flat organizational structure and collaborative management style gives everyone the opportunity to take initiative without any middle managers getting in the way.'
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<Text type="section" align="center">
						Some Super Things About Working Here
					</Text>
					<ul classes={css.list}>
						<li classes={css.item}>
							<img classes={css.itemIcon} src={commuteIcon} alt="" />
							<Text type="subhead">Zero Commute Time</Text>
							<Text type="body">
								The only traffic you’ll ever get stuck in is a series of tubes.
							</Text>
						</li>
						<li classes={css.item}>
							<img classes={css.itemIcon} src={vacationIcon} alt="" />
							<Text type="subhead">Seven Weeks Paid Vacation</Text>
							<Text type="body">
								With one month of personal time off plus a week of sick time plus
								ten paid holidays per year, you can finally test that model rocket.
							</Text>
						</li>
						<li classes={css.item}>
							<img classes={css.itemIcon} loading="lazy" src={pensionIcon} alt="" />
							<Text type="subhead">401(K) With Matching</Text>
							<Text type="body">
								Our plan administrator thought we were nuts to do a match. He also
								thought he was a squirrel. Kinda awkward.
							</Text>
						</li>
						<li classes={css.item}>
							<img classes={css.itemIcon} src={insuranceIcon} alt="" />
							<Text type="subhead">Really Good Health Insurance</Text>
							<Text type="body">
								Once our fountain of youth is finished, we hope to save a bundle on
								healthcare.
							</Text>
						</li>
						<li classes={css.item}>
							<img classes={css.itemIcon} loading="lazy" src={visionIcon} alt="" />
							<Text type="subhead">Vision Plan</Text>
							<Text type="body">We took a vote and the ayes have it.</Text>
						</li>
						<li classes={css.item}>
							<img classes={css.itemIcon} loading="lazy" src={dentalIcon} alt="" />
							<Text type="subhead">Dental Discounts</Text>
							<Text type="body">
								With all the sweet things you’ll be doing, our dental plan makes
								sure the sugar won’t rot your teeth.
							</Text>
						</li>
						<li classes={css.item}>
							<img classes={css.itemIcon} loading="lazy" src={spendingIcon} alt="" />
							<Text type="subhead">Flexible Spending Account</Text>
							<Text type="body">
								We’re not saying you’re accident-prone, but we do want to make sure
								all those first aid kits won’t lead to bankruptcy.
							</Text>
						</li>
						<li classes={css.item}>
							<img
								classes={css.itemIcon}
								loading="lazy"
								src={lifeinsuranceIcon}
								alt=""
							/>
							<Text type="subhead">Life Insurance</Text>
							<Text type="body">
								Our loved ones were tired of us walking around in bubble wrap.
							</Text>
						</li>
						<li classes={css.item}>
							<img
								classes={css.itemIcon}
								loading="lazy"
								src={parentalleaveIcon}
								alt=""
							/>
							<Text type="subhead">Parental Leave</Text>
							<Text type="body">
								Take some time off to welcome that new miniature human into your
								life.
							</Text>
						</li>
						<li classes={css.item}>
							<img classes={css.itemIcon} loading="lazy" src={internetIcon} alt="" />
							<Text type="subhead">Internet Allowance</Text>
							<Text type="body">
								We tried carrier pigeons, but this newfangled internet thing was
								much faster.
							</Text>
						</li>
						<li classes={css.item}>
							<img
								classes={css.itemIcon}
								loading="lazy"
								src={officesubsidyIcon}
								alt=""
							/>
							<Text type="subhead">Home Office Subsidy</Text>
							<Text type="body">
								What you need to work from home. You’re on your own for acquiring
								cats.
							</Text>
						</li>
						<li classes={css.item}>
							<img classes={css.itemIcon} loading="lazy" src={hoursIcon} alt="" />
							<Text type="subhead">Flexible Hours</Text>
							<Text type="body">
								You’re the boss of your time. For your own sake, we hope you’re not
								a bad boss.
							</Text>
						</li>
						<li classes={css.item}>
							<img classes={css.itemIcon} loading="lazy" src={loveIcon} alt="" />
							<Text type="subhead">Do What You Love</Text>
							<Text type="body">
								We are, of course, referring to developing amazing Web apps. We
								don’t really need a lepidopterist.
							</Text>
						</li>
						<li classes={css.item}>
							<img classes={css.itemIcon} loading="lazy" src={peopleIcon} alt="" />
							<Text type="subhead">A team you'll love</Text>
							<Text type="body">
								With their spectacular wit, intellect, and good looks, SitePenners
								are one of the best reasons to work here.
							</Text>
						</li>
					</ul>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<Text type="section" align="center">
						We're on the lookout for...
					</Text>
					<div classes={css.openPositions}>
						{jobs.length ? (
							<GridColumns columnCount={jobs.length && jobs.length > 1 ? 2 : 1}>
								{jobs.map((job) => (
									<CareerPosition title={job.name} route={job.route}>
										{job.description}
									</CareerPosition>
								))}
							</GridColumns>
						) : (
							<Text>
								There are no open positions at this time. Please check back soon!
							</Text>
						)}
					</div>
				</ContentWrapper>
			</Section>
		</div>
	);
});

export default Careers;
